import axios from "axios";
import { ElMessage } from "element-plus";
import { clearAuth, clearUserInfoCache, getToken } from "@utils/user";
import Bus from "@utils/bus.js";

let authRedirecting = false;

const getResponseMessage = (data = {}) =>
  data?.msg || data?.message || data?.data || "";

const isAuthInvalidCode = (code) => [401, 403].includes(Number(code));

const redirectToHomeLogin = (message) => {
  // token 过期/鉴权失败时，统一清理缓存（包含 token 的 expiresAt 信息）
  clearAuth();
  clearUserInfoCache();
  window.dispatchEvent(new Event("clearStorageEvent"));
  Bus.$emit("authStateChanged", { isLogin: false });

  if (authRedirecting) {
    return;
  }
  authRedirecting = true;

  if (message) {
    ElMessage.error(message);
  }

  const openLogin = () => {
    setTimeout(() => {
      Bus.$emit("changeLoginShow", true);
      authRedirecting = false;
    }, 100);
  };

  if (window.router) {
    window.router
      .replace({ name: "HomeShop" })
      .finally(openLogin);
    return;
  }

  window.location.href = "/";
};

const handleAuthInvalid = (
  data = {},
  fallbackMessage = "登录状态异常，请重新登录",
) => {
  redirectToHomeLogin(getResponseMessage(data) || fallbackMessage);
};

// 统一的异常提示处理函数
const handleError = (error) => {
  const status = error?.response?.status;
  if (isAuthInvalidCode(status)) {
    if (error?.config?.skipAuthHandling) {
      return Promise.reject(error);
    }
    handleAuthInvalid(
      error?.response?.data,
      status === 401 ? "登录状态已过期，请重新登录" : "登录状态异常，请重新登录",
    );
    return Promise.reject(error);
  }

  if (error?.config?.skipGlobalErrorMessage) {
    return Promise.reject(error);
  }
  // 区分不同的错误类型给出对应的提示
  if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText;
    switch (status) {
      case 404:
        ElMessage.error("请求的接口不存在");
        break;
      case 500:
        ElMessage.error("服务器内部错误，请稍后重试");
        break;
      case 502:
        ElMessage.error("网关错误，请稍后重试");
        break;
      default:
        ElMessage.error(`请求失败 [${status}]：${statusText}`);
    }
  } else if (error.request) {
    // 请求已发送，但没有收到响应（网络问题、超时等）
    if (error.code === "ECONNABORTED") {
      ElMessage.error("请求超时，请检查网络或稍后重试");
    } else {
      ElMessage.error("网络连接异常，请检查您的网络设置");
    }
  } else {
    // 请求配置/处理过程中发生的错误
    ElMessage.error(`请求出错：${error.message}`);
  }
  // 返回错误对象，方便业务层进一步处理
  return Promise.reject(error);
};

const handleResponse = (res) => {
  if (isAuthInvalidCode(res?.data?.code) && !res?.config?.skipAuthHandling) {
    handleAuthInvalid(
      res?.data,
      res.data.code === 401 ? "登录状态已过期，请重新登录" : "登录状态异常，请重新登录",
    );
    return Promise.reject(res);
  }
  return res;
};

// 默认配置
const defConfig = {
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  // timeout: 300000000000, // 默认超时时间，避免无限制等待
  transformRequest: [
    function (data, headers) {
      return JSON.stringify(data);
    },
  ],
  transformResponse: [
    (data) => {
      let parseData = null;
      try {
        parseData = JSON.parse(data);
        return parseData;
      } catch {
        return data;
      }
    },
  ],
};

// 带 token 的默认请求实例
export const defHttp = axios.create(defConfig);
defHttp.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers["Authorization"] = token;
    }
    return config;
  },
  (error) => {
    handleError(error); // 处理请求阶段的错误
    return Promise.reject(error);
  },
);
defHttp.interceptors.response.use(
  (res) => handleResponse(res), // 成功响应直接返回
  (error) => handleError(error), // 统一处理响应错误
);

// 不带 token 的请求实例
const noTokenConfig = {
  headers: { "Content-Type": "application/json" },
  // timeout: 300000000000,
  transformRequest: [
    function (data) {
      return JSON.stringify(data);
    },
  ],
};
export const noTokenHttp = axios.create(noTokenConfig);
noTokenHttp.interceptors.response.use(
  (res) => {
    if (res.data.code === 401) {
      redirectToHomeLogin("请登录后再操作");
    }
    return res;
  },
  (error) => handleError(error), // 统一处理网络异常
);

// 文件上传/下载请求实例
const fileConfig = {
  headers: { "Content-Type": "multipart/form-data" },
  // timeout: 60000, // 文件请求超时时间更长
  transformRequest: [
    function (data, headers) {
      const token = getToken();
      if (token) {
        headers["Authorization"] = token;
      }
      return data;
    },
  ],
};
export const fileHttp = axios.create(fileConfig);
fileHttp.interceptors.response.use(
  (res) => {
    const authResponse = handleResponse(res);
    if (authResponse !== res) {
      return authResponse;
    }
    if (res.config?.rawResponse) {
      return res;
    }
    if (res.data.code === 200) {
      return res;
    } else if (res.data.code === undefined) {
      return res.data;
    } else {
      console.log("返回非200状态", res);
      ElMessage.error(res.data.msg || "文件操作失败");
    }
    return res.data.data;
  },
  (error) => handleError(error), // 统一处理网络异常
);

// 通用带 Bearer token 的请求实例
export const http = axios.create({
  timeout: 30000,
});
http.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = token.startsWith("Bearer ") ? token : "Bearer " + token;
      return config;
    } else {
      redirectToHomeLogin("请先登录");
      return Promise.reject(new Error("未登录"));
    }
  },
  (error) => {
    handleError(error);
    return Promise.reject(error);
  },
);
http.interceptors.response.use(
  (res) => {
    const authResponse = handleResponse(res);
    if (authResponse !== res) {
      return authResponse;
    }
    // 商品保存需要校验外层和内层业务码，按需保留完整响应。
    if (res.config?.rawResponse) return res;
    if (res.data.code === 200) {
      return res.data.data;
    } else {
      console.log("返回非200状态", res);
      ElMessage.error(res.data.msg || "请求失败");
    }
    return res.data.data;
  },
  (error) => handleError(error), // 统一处理网络异常
);
